import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchExportJobs,
  isExportJobActive,
  isExportJobReadyToDownload,
  isExportJobTerminal,
  type ExportJob,
} from '../apiClient/fetchExportJobs';
import { resolveExportPollIntervalMs } from '../utils/exportPollInterval';

const EXPORT_JOBS_SUMMARY_KEY = ['export_jobs', 'summary'] as const;

export interface WatchedExportEntry {
  jobId: string;
  job: ExportJob | null;
}

interface ExportJobsContextValue {
  summaryJobs: ExportJob[];
  watchedEntries: WatchedExportEntry[];
  activeCount: number;
  readyCount: number;
  watchedJobIds: ReadonlySet<string>;
  watchJob: (jobId: string) => void;
  dismissWatchedJob: (jobId: string) => void;
  invalidateExportJobs: () => void;
  isSummaryLoading: boolean;
  isSummaryFetching: boolean;
  showDownloadStatusModal: boolean;
  openDownloadStatusModal: () => void;
  closeDownloadStatusModal: () => void;
  getJobStatus: (jobId: string) => ExportJob['status'] | undefined;
  getJobById: (jobId: string) => ExportJob | undefined;
}

const ExportJobsContext = createContext<ExportJobsContextValue | null>(null);

function countActive(jobs: ExportJob[]): number {
  return jobs.filter(isExportJobActive).length;
}

function countReady(jobs: ExportJob[]): number {
  return jobs.filter(isExportJobReadyToDownload).length;
}

export function ExportJobsProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const watchedJobIdsRef = useRef<Set<string>>(new Set());
  const watchedAtByJobIdRef = useRef<Map<string, number>>(new Map());
  const [, bumpWatched] = useState(0);
  const [showDownloadStatusModal, setShowDownloadStatusModal] = useState(false);

  const hasWatched = watchedJobIdsRef.current.size > 0;

  const watchJob = useCallback((jobId: string) => {
    if (!jobId) return;
    watchedJobIdsRef.current.add(jobId);
    watchedAtByJobIdRef.current.set(jobId, Date.now());
    bumpWatched((n) => n + 1);
    void queryClient.invalidateQueries({ queryKey: ['export_jobs'] });
  }, [queryClient]);

  const dismissWatchedJob = useCallback((jobId: string) => {
    if (!watchedJobIdsRef.current.delete(jobId)) return;
    watchedAtByJobIdRef.current.delete(jobId);
    bumpWatched((n) => n + 1);
  }, []);

  const shouldKeepPolling = useCallback(
    (jobs: ExportJob[]) => {
      if (jobs.some(isExportJobActive)) {
        return true;
      }
      return Array.from(watchedJobIdsRef.current).some((jobId) => {
        const job = jobs.find((j) => j.id === jobId);
        return !job || !isExportJobTerminal(job);
      });
    },
    [],
  );

  const summaryQuery = useQuery({
    queryKey: [...EXPORT_JOBS_SUMMARY_KEY, hasWatched ? 'recent' : 'active'],
    queryFn: () =>
      fetchExportJobs({
        activeOnly: !hasWatched,
        pageSize: 25,
      }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    refetchInterval: (query) => {
      const jobs = query.state.data?.results ?? [];
      return resolveExportPollIntervalMs(jobs, {
        shouldPoll: shouldKeepPolling(jobs),
        watchedJobIds: watchedJobIdsRef.current,
        watchedAtByJobId: watchedAtByJobIdRef.current,
      });
    },
  });

  const summaryJobs = summaryQuery.data?.results ?? [];
  const activeCount = countActive(summaryJobs);
  const readyCount = countReady(summaryJobs);

  const invalidateExportJobs = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['export_jobs'] });
  }, [queryClient]);

  const openDownloadStatusModal = useCallback(() => {
    setShowDownloadStatusModal(true);
  }, []);

  const closeDownloadStatusModal = useCallback(() => {
    setShowDownloadStatusModal(false);
  }, []);

  const getJobById = useCallback(
    (jobId: string) => summaryJobs.find((j) => j.id === jobId),
    [summaryJobs],
  );

  const getJobStatus = useCallback(
    (jobId: string) => getJobById(jobId)?.status,
    [getJobById],
  );

  const watchedJobIds = useMemo(
    () => new Set(watchedJobIdsRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bumpWatched syncs Set mutations
    [summaryJobs, summaryQuery.dataUpdatedAt, hasWatched],
  );

  const watchedEntries = useMemo<WatchedExportEntry[]>(() => {
    return Array.from(watchedJobIds).map((jobId) => ({
      jobId,
      job: summaryJobs.find((j) => j.id === jobId) ?? null,
    }));
  }, [watchedJobIds, summaryJobs]);

  const value = useMemo<ExportJobsContextValue>(
    () => ({
      summaryJobs,
      watchedEntries,
      activeCount,
      readyCount,
      watchedJobIds,
      watchJob,
      dismissWatchedJob,
      invalidateExportJobs,
      isSummaryLoading: summaryQuery.isLoading,
      isSummaryFetching: summaryQuery.isFetching,
      showDownloadStatusModal,
      openDownloadStatusModal,
      closeDownloadStatusModal,
      getJobStatus,
      getJobById,
    }),
    [
      summaryJobs,
      watchedEntries,
      activeCount,
      readyCount,
      watchedJobIds,
      watchJob,
      dismissWatchedJob,
      invalidateExportJobs,
      summaryQuery.isLoading,
      summaryQuery.isFetching,
      showDownloadStatusModal,
      openDownloadStatusModal,
      closeDownloadStatusModal,
      getJobStatus,
      getJobById,
    ],
  );

  return (
    <ExportJobsContext.Provider value={value}>
      {children}
    </ExportJobsContext.Provider>
  );
}

export function useExportJobs(): ExportJobsContextValue {
  const ctx = useContext(ExportJobsContext);
  if (!ctx) {
    throw new Error('useExportJobs must be used within ExportJobsProvider');
  }
  return ctx;
}

export { EXPORT_JOBS_SUMMARY_KEY };
export {
  POLL_FAST_MS,
  POLL_NORMAL_MS,
  POLL_FAST_WINDOW_MS,
} from '../utils/exportPollInterval';
